import { faFilter } from "@fortawesome/free-solid-svg-icons";
import {
  GSListPage,
  GSPageSection,
  defaultPadding,
  defaultLayouts,
  GSEmptyList,
} from "golfstatus_react_components";

import { useEffect, useMemo } from "react";
import { useMatch } from "react-router-dom";
import { Outlet } from "react-router-dom";
import "./event-sponsorships.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  getTournamentSponsors,
  getTournamentSponsorshipTiers,
  selectTournamentSponsors,
  selectTournamentSponsorshipTiers,
} from "../../../../reducers/tournamentSlice";
import { useHeaderStyle, useSectionStyles } from "../../../../hooks/themeHooks";
import { getRouting } from "../../../../helpers/Converters";
import { eventBaseRoute, TOURNAMENT_ID } from "../../../../routes/event";
import PageHeader from "../../../page-header";
import BecomeASponsorWidget from "../../../widgets/become-a-sponsor-widget";
import { useTechSponsors } from "../../../widgets/tech-sponsor-widget";
import AllSponsorsWidget from "../../../widgets/all-sponsors-widget";
import AvailableSponsorshipsWidget from "../../../widgets/available-sponsorships-widget";
import { useNavigationButtons } from "../../../../hooks/pageHooks";
import { usePermissions } from "../../../../hooks/permissionHooks";
import {
  useLoading,
  useTournament,
  useTournamentPackages,
} from "../../../../hooks/tournamentHooks";

const EventSponsorships = (props) => {
  const tournament = useTournament();
  const sponsorships = useSelector(selectTournamentSponsors);
  const sponsorshipTiers = useSelector(selectTournamentSponsorshipTiers);
  const { sponsorPackages } = useTournamentPackages(true);

  const route = getRouting(eventBaseRoute, [
    TOURNAMENT_ID,
    "sponsorships",
    "*",
  ]);
  const match = useMatch(route);
  const tournamentID = tournament?.id;

  const [isLoading] = useLoading([
    "getPublicTournamentDetails",
    "getTournamentSponsors",
  ]);
  const dispatch = useDispatch();

  const { defaultSectionStyle, headerSectionStyle } = useSectionStyles();

  const { headerStyle } = useHeaderStyle();

  const techSponsors = sponsorships.filter(
    (sponsor) => sponsor.sponsorshipType === "leaderboard"
  );

  const techTier = sponsorshipTiers?.find?.(
    (tier) => tier?.sponsorshipTierType === "technology"
  );

  const getPageActions = useNavigationButtons("sponsorships");

  const permissions = usePermissions();

  const tierSponsors = useMemo(() => {
    if (!sponsorshipTiers) {
      return;
    }
    const sortedTiers = [...sponsorshipTiers]?.sort(
      (a, b) => a.sortOrder - b.sortOrder
    );

    return sortedTiers
      ?.map((tier) => {
        const sponsors = sponsorships
          .filter((s) => s?.sponsorshipTierId === tier?.id)
          .sort((a, b) => a.sortOrder - b.sortOrder);

        return {
          tier,
          sponsors: sponsors,
        };
      })
      .filter((st) => st.tier.sponsorshipTierType !== "technology")
      .filter((st) => st.sponsors.length !== 0);
  }, [sponsorshipTiers, sponsorships]);

  useEffect(() => {
    if (tournamentID) {
      dispatch(getTournamentSponsors(tournamentID));
      dispatch(getTournamentSponsorshipTiers(tournamentID));
    }
  }, [dispatch, tournamentID]);

  const getSearchActions = () => {
    return [
      {
        id: 1,
        type: "mobile-icon",
        actionIcon: faFilter,
      },
    ];
  };

  const isSidePanelOpen = () => {
    //remove this when you add your match route.
    //
    return match?.params?.["*"] !== "";
  };

  const getRouter = () => {
    return <Outlet />;
  };

  const hasTechSponsor = () => {
    return (
      techSponsors?.length > 0 ||
      !!tournament?.tournamentPromotion?.secondaryEventPhoto
    );
  };

  const [getTechSponsorSections] = useTechSponsors();

  const getEmptyList = () => {
    return (
      <GSEmptyList
        style={{
          ...defaultPadding.emptyListPad,
          ...defaultLayouts.fullWidth,
        }}
        title="No Sponsors Yet"
        detail={`${tournament?.name} does not have any sponsors.`}
      />
    );
  };

  const getItemList = () => {
    return (
      <div style={{ ...defaultLayouts.vStack }}>
        <PageHeader header="Sponsors" />

        {permissions?.packagesEnabled() && sponsorPackages?.length > 0 ? (
          <GSPageSection
            {...headerSectionStyle}
            body={[<BecomeASponsorWidget />]}
          />
        ) : null}

        {hasTechSponsor() ? (
          <GSPageSection
            title={techTier?.name}
            {...defaultSectionStyle}
            body={getTechSponsorSections()}
          />
        ) : null}

        {sponsorships.length > 0 ? (
          tierSponsors?.map?.(({ sponsors, tier }) => {
            return (
              <GSPageSection
                key={tier.id}
                title={tier.name}
                {...defaultSectionStyle}
                body={[<AllSponsorsWidget sponsorList={sponsors} />]}
              />
            );
          })
        ) : (
          <GSPageSection body={[getEmptyList()]} />
        )}

        {sponsorPackages.length > 0 && permissions.packagesEnabled() ? (
          <GSPageSection
            title="Available Sponsorships"
            style={defaultSectionStyle.style}
            bodyStyle={{
              ...defaultSectionStyle.bodyStyle,
              ...defaultPadding.noPad,
            }}
            body={[<AvailableSponsorshipsWidget />]}
          />
        ) : null}
      </div>
    );
  };

  return (
    <event-sponsorships>
      <GSListPage
        title=""
        getPageActions={getPageActions}
        getSearchActions={getSearchActions}
        getItemList={getItemList}
        isSidePanelOpen={isSidePanelOpen}
        getRouter={getRouter}
        headerStyle={headerStyle}
        loading={isLoading}
      ></GSListPage>
    </event-sponsorships>
  );
};

export default EventSponsorships;
